import React, { useState, useRef } from 'react';
import { Camera, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useUploadManager } from '../../context/UploadManagerContext';

// Resolve a stored image path/url into a URL usable in an <img> tag.
// Uploaded images are stored as relative `/api/uploads/...` paths on the
// backend container's disk. We deliberately return those as relative URLs so
// they always resolve against the current origin — this way a production
// deploy fetches from the production backend (which actually has the file)
// instead of an old preview URL baked in at build time.
//
// IMPORTANT: returns `undefined` (not the empty string) when there is no
// value. Passing `<img src={undefined}>` causes React to omit the attribute
// entirely — whereas `<img src="">` triggers a browser "empty string" warning
// and issues a spurious network request back to the current page URL.
export const resolveImage = (val) => {
  if (!val) return undefined;
  if (val.startsWith('data:') || val.startsWith('http')) return val;
  if (val.startsWith('/api/')) return val;              // same-origin, ingress routes it
  return val;
};

const ImageUpload = ({ src, alt, onChange, className = '', ratio = 'aspect-square' }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const mgr = useUploadManager();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large (max 5MB)');
      return;
    }
    setBusy(true);
    // Delegate the actual PUT to the global background upload manager. The
    // returned promise is still awaitable here so the caller can pipe the URL
    // into onChange, but if the component unmounts (e.g. admin navigates
    // away mid-upload) the request keeps running in the background and the
    // <UploadTray/> shows progress + eventual "Successfully saved" toast.
    try {
      const { promise } = mgr.upload(file, { label: file.name });
      const data = await promise;
      if (data?.url) await onChange(data.url);
    } catch {
      /* toast + tray already surface the error */
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className={`relative ${ratio} bg-neutral-100 border border-neutral-200 rounded-sm overflow-hidden group cursor-pointer ${className}`} onClick={() => inputRef.current?.click()}>
      {src ? (
        <img src={resolveImage(src)} alt={alt} className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-neutral-400 text-xs">No image</div>
      )}
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center text-white transition-opacity">
        {busy ? <Loader2 className="w-6 h-6 animate-spin" /> : (
          <>
            <Camera className="w-6 h-6 mb-1" />
            <span className="text-xs font-semibold">Click to upload</span>
          </>
        )}
      </div>
      <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
    </div>
  );
};

export default ImageUpload;
